// Reporting WIDGETS — the renderer registry and the region both dashboards drop
// in. The component half of the framework whose pure half is the leaf
// src/lib/reporting/widgets.ts.
//
// A widget is a user-added instance: pick a METRIC and pick how it is
// REPRESENTED. Phase 1 registers two representations, both of which run off
// summary/trend data the dashboards ALREADY fetch — a large `singleNumber`
// scorecard and a `trend` chart. Phase 2's dimensional types (pie, ranked bar,
// funnel, gauge) become extra entries in `WIDGET_RENDERERS` and the leaf's
// catalogue; nothing here is shaped around there being exactly two.
//
// ⚠️ ONE FRAMEWORK, PARAMETERISED — NOT FORKED PER DASHBOARD. Meta and Google
// have entirely different metric vocabularies, formatters and summary shapes, so
// the dashboard hands in a `WidgetDataSource`: its metric catalogue, and
// functions that resolve a metric key to an already-formatted number or to a
// trend series. That is the same posture ReportingChartGrid /
// ReportingMiniChart / ChartStyleToggle already take, and it is why the widget
// on the Google page and the widget on the Meta page cannot drift apart.
//
// ⚠️ THE FORMATTING STAYS WITH THE DASHBOARD. `WidgetNumber.value` arrives
// already rendered by that dashboard's own formatter — `formatMetricValue` /
// `formatReportingAmount` on Meta, `formatMetric` on Google — because those are
// where the per-currency rule ("A$60,176 · NZ$28,927", never a blended total)
// and the unmeasured-is-an-em-dash rule already live. Re-deriving either here
// would be a second implementation of two rules that exist to stop us telling a
// client something untrue about their money.

import { useRef, useState } from 'react';
import { Plus, Settings2, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useClickOutside } from '../../lib/useClickOutside';
import { cn } from '../../lib/utils';
import { Cell, Pie, PieChart } from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import {
  clampTopN,
  createWidget,
  isWidgetVizType,
  MAX_WIDGET_TOP_N,
  MIN_WIDGET_TOP_N,
  removeWidget,
  updateWidget,
  WIDGET_VIZ_TYPES,
  widgetVizTypeDef,
  type ReportingWidget,
  type WidgetVizType,
} from '../../lib/reporting/widgets';
import {
  canPlotDimensional,
  clampPercent,
  funnelRatios,
  planSlices,
  unmeasuredCount,
} from '../../lib/reporting/widgetSlices';
import { canvasPanelKey } from '../../lib/reporting/chartLayout';
import { ChartGripHandle, type ChartPanel } from './ReportingChartGrid';
import { ChartStyleToggle } from './ChartStyleToggle';
import { PanelTitle } from './PanelTitle';
import { ReportingMiniChart } from './ReportingMiniChart';
import { deltaColorClass, type HeroDelta } from './heroCards';
import type { ChartStyle } from './chartPrefs';
import { formatMetricValue } from './trendSeries';
import type { MetricTone, TrendChartData, TrendMetricFormat } from './trendSeries';

// ---------------------------------------------------------------------------
// What a dashboard hands in
// ---------------------------------------------------------------------------

/** One offerable metric, from the host dashboard's own catalogue. */
export interface WidgetMetricOption {
  key: string;
  label: string;
  hint?: string;
}

/** A metric resolved for the single-number scorecard. */
export interface WidgetNumber {
  label: string;
  /**
   * The formatted value line — per-currency for money, "—" when unmeasured.
   * Formatted by the DASHBOARD, see the header note.
   */
  value: string;
  /**
   * True when the metric has no measurement in this scope. Drives the muted
   * render; it is NOT the same as a measured zero, and must never become one.
   */
  unmeasured: boolean;
  /** Which direction is good — drives the delta colour (a falling cost is green). */
  tone: MetricTone;
  /** vs the previous equivalent period; null when there is nothing to compare. */
  delta: HeroDelta | null;
}

export interface WidgetDataSource {
  /** Everything the config popover may offer, in catalogue order. */
  metrics: readonly WidgetMetricOption[];
  /** The scorecard view of a metric over the current scope. */
  number: (metricKey: string) => WidgetNumber;
  /** The metric's trend series over the current scope + grouping. */
  trend: (metricKey: string) => TrendChartData;
  /**
   * The metric's chart style. Deliberately the dashboard's EXISTING per-metric
   * style preference (the one its KPI tile already uses) rather than a new
   * per-widget field: Phase 1 pins the widget shape at four fields, and a metric
   * being drawn the same way wherever it appears is the better default anyway.
   */
  style: (metricKey: string) => ChartStyle;
  onStyleChange: (metricKey: string, style: ChartStyle) => void;
  /**
   * False when this dashboard has no previous-period figures at all. The
   * comparison toggle then renders DISABLED with the reason rather than
   * vanishing — an AM must see that the comparison exists and is unavailable,
   * not wonder whether the feature is missing.
   */
  comparisonAvailable: boolean;
  comparisonUnavailableReason?: string;
  loading: boolean;

  // ── Phase 2 ──────────────────────────────────────────────────────────────

  /**
   * The three funnel stages for THIS dashboard, already labelled and in order.
   * The dashboard names them because the vocabulary differs — Meta's middle
   * stage is link clicks and its last is leads; Google's are clicks and
   * conversions — and inventing a shared name for both would be wrong on each.
   */
  funnel: () => WidgetFunnelStage[];
  /**
   * The metrics a GAUGE may show: the percentage ones, and only those. A dial
   * runs 0–100; putting a cost or a click count on one would be meaningless.
   */
  percentMetrics: readonly WidgetMetricOption[];
  /** A percentage metric's value (0–100), or null when it was not measured. */
  percent: (metricKey: string) => number | null;
  /**
   * The dimensions this dashboard can already split by WITHOUT a new query.
   * Empty is legitimate — the dimensional widgets then say so rather than
   * rendering an empty chart.
   */
  dimensions: readonly WidgetDimensionOption[];
  /** One metric split by one dimension, ready to plot. */
  dimensional: (metricKey: string, dimension: string) => WidgetDimensionalData;
}

/** One stage of the fixed impressions → clicks → conversions funnel. */
export interface WidgetFunnelStage {
  label: string;
  /** Null when the dashboard could not measure this stage — never rendered as 0. */
  value: number | null;
}

export interface WidgetDimensionOption {
  key: string;
  label: string;
}

/**
 * One slice / bar: a dimension value and this metric's figure for it.
 *
 * ⚠️ `null` means the metric was NOT REPORTED for that value — the dashboard
 * passes it through rather than substituting 0, and `planSlices` drops it. A
 * zero-height bar would read as a real measurement of nothing.
 */
export interface WidgetSlice {
  key: string;
  label: string;
  value: number | null;
}

/**
 * A metric split by a dimension.
 *
 * ⚠️⚠️ `mixedCurrency` IS THE INTEGRITY FLAG, and it is why this carries a
 * status rather than just rows. A "cost by location" pie whose slices are AUD
 * added to NZD is a fabricated number on a client's report — the same class of
 * error as the Phase-1 delta refusal. When the scope holds more than one
 * currency and the metric is money, the dashboard sets this and the widget
 * REFUSES TO PLOT, saying why. It never falls back to a blended total.
 */
export interface WidgetDimensionalData {
  slices: WidgetSlice[];
  /** The currency the values are in, for money formatting. Null for counts/ratios. */
  currency: string | null;
  /** True when the metric is money and the scope spans several currencies. */
  mixedCurrency: boolean;
  /** The currencies in scope — named in the refusal so the user can narrow. */
  currencies: readonly string[];
  format: TrendMetricFormat;
  /** Set when this dimension has no data at all (rather than an empty result). */
  unavailable?: string;
  loading: boolean;
}

// ---------------------------------------------------------------------------
// The value line — shared by BOTH widget types
// ---------------------------------------------------------------------------

/**
 * The metric's value and its optional vs-previous-period delta.
 *
 * ⚠️ ONE RENDERING, used by the single number AND by the trend widget's header,
 * so a metric's figure reads identically wherever it appears — which is the same
 * reason the KPI tiles pair a value with their mini-chart rather than showing a
 * bare line. Only the TYPE SIZE differs between the two: a single number scales
 * with its box, a trend header stays at the tile's size so the chart keeps the
 * room.
 *
 * The rules it enforces are the dashboard's, not this component's: the value
 * arrives already formatted (money per-currency, "—" when unmeasured), and the
 * delta is coloured by TONE AND DIRECTION together so a falling cost is green.
 */
function WidgetValueLine({
  widget,
  source,
  valueClassName,
  deltaClassName,
  showDeltaCaption = true,
}: {
  widget: ReportingWidget;
  source: WidgetDataSource;
  valueClassName?: string;
  deltaClassName?: string;
  /** The trend header is tight on width — it drops the "vs previous period" tail. */
  showDeltaCaption?: boolean;
}): React.JSX.Element {
  const data = source.number(widget.metricKey);
  const delta = widget.showComparison ? data.delta : null;

  return (
    <>
      {source.loading && data.unmeasured ? (
        <span className="inline-block h-8 w-32 animate-pulse rounded bg-muted" />
      ) : (
        <div
          className={cn(
            'font-semibold leading-tight tabular-nums',
            // ⚠️ Unmeasured renders muted and as the em dash the dashboard's
            // formatter already produced — never as 0. Telling a client they got
            // zero when we could not measure is a materially different claim.
            data.unmeasured ? 'text-muted-foreground' : 'text-foreground',
            valueClassName,
          )}
        >
          {data.value}
        </div>
      )}
      {delta && (
        <div
          className={cn(
            'mt-1 font-medium',
            // Colour by DIRECTION AND TONE together, so a falling cost is green.
            deltaColorClass(data.tone, delta.direction),
            deltaClassName,
          )}
          title="vs the previous equivalent period"
        >
          {delta.direction === 'up' ? '▲' : delta.direction === 'down' ? '▼' : '–'}{' '}
          {Math.abs(delta.pct)}%
          {showDeltaCaption && (
            <span className="ml-1 font-normal text-muted-foreground">vs previous period</span>
          )}
        </div>
      )}
      {widget.showComparison && !delta && !source.loading && (
        // Saying nothing here would read as "no change". The comparison was
        // asked for and could not be made — say which.
        <div className={cn('mt-1 text-xs text-muted-foreground', deltaClassName)}>
          {source.comparisonAvailable
            ? 'no previous period to compare'
            : (source.comparisonUnavailableReason ?? 'comparison unavailable')}
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// The single-number widget — the priority
// ---------------------------------------------------------------------------

/**
 * A large scorecard for one metric: label, the value, and optionally the change
 * vs the previous period.
 *
 * ⚠️ It FILLS ITS BOX. The value block grows into whatever the panel was dragged
 * to and the figure itself scales with the box — see `.reporting-widget-fill` in
 * index.css, which sizes the type in container units. Without that a taller
 * panel would just add blank space under an unchanged number, which is the same
 * failure the chart panel-fill CSS exists to prevent. No recharts remount nonce
 * is needed here: there is no canvas, only text.
 */
function SingleNumberWidget({
  widget,
  source,
}: {
  widget: ReportingWidget;
  source: WidgetDataSource;
}): React.JSX.Element {
  return (
    <div className="reporting-widget-fill flex min-h-[3.5rem] flex-col items-start justify-center">
      <WidgetValueLine
        widget={widget}
        source={source}
        valueClassName="reporting-widget-value w-full text-4xl"
        deltaClassName="reporting-widget-delta text-sm"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// The trend widget
// ---------------------------------------------------------------------------

/**
 * The chosen metric over time, through the SAME chart the KPI tiles draw
 * (ReportingMiniChart) — so money stays per-currency, ratios recompute per
 * bucket and an unmeasured bucket is a gap rather than a zero, with no second
 * set of chart rules to keep in step.
 *
 * ⚠️ IT LEADS WITH THE NUMBER, exactly as a KPI tile does. Without the value
 * header this was a bare line with no figure attached — the reader could see the
 * shape but not what it was worth, and had to go and find the tile to learn the
 * number. The header reuses the SAME `WidgetValueLine` the single-number widget
 * renders (money per-currency, delta by tone, "—" when unmeasured), at the
 * tile's type size so the chart keeps the room.
 *
 * The style toggle writes through to the metric's EXISTING style preference, so
 * a metric is drawn the same way in its widget and in its tile.
 */
function TrendWidget({
  widget,
  source,
}: {
  widget: ReportingWidget;
  source: WidgetDataSource;
}): React.JSX.Element {
  const data = source.trend(widget.metricKey);
  return (
    <div className="flex flex-1 flex-col">
      {/* The value header — the same rendering as the single number, at the KPI
          tile's size. Shown even when the range is empty: "—" is a fact about
          the metric, and hiding it would leave the panel with nothing but a
          message. */}
      <WidgetValueLine
        widget={widget}
        source={source}
        valueClassName="text-2xl"
        deltaClassName="text-xs"
        showDeltaCaption={false}
      />
      {data.isEmpty ? (
        <div className="flex min-h-[3rem] flex-1 items-center text-sm text-muted-foreground">
          {source.loading ? 'loading…' : 'No data in this range.'}
        </div>
      ) : (
        // `reporting-chart-panel-grow` is the explicit opt-in that carries the
        // panel's flex chain down to the canvas — see the note in index.css;
        // without it a resized panel grows around an unchanged chart.
        <div className="reporting-chart-panel-grow mt-2">
          <ReportingMiniChart data={data} style={source.style(widget.metricKey)} />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Phase 2 — funnel, gauge (summary data) and the dimensional pair
// ---------------------------------------------------------------------------

/** A short "we can't draw this, and here's why" panel — never a blank box. */
function WidgetNotice({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <div className="flex flex-1 items-center py-2 text-xs text-muted-foreground">{children}</div>
  );
}

/**
 * Impressions → clicks → conversions, as tapering bars.
 *
 * Hand-drawn rather than recharts' FunnelChart: three bars whose widths are a
 * ratio need no axis system, and this way the drop-off percentage sits ON the
 * step it describes, which is the number a client actually reads.
 *
 * ⚠️ AN UNMEASURED STAGE SHOWS "—" AND NO BAR, never a zero-width one that reads
 * as "nobody clicked". See funnelRatios.
 */
function FunnelWidget({ source }: { widget: ReportingWidget; source: WidgetDataSource }) {
  const stages = source.funnel();
  const ratios = funnelRatios(stages.map((s) => s.value));
  const measured = stages.some((s) => s.value !== null);

  if (!measured) {
    return <WidgetNotice>{source.loading ? 'loading…' : 'No data in this range.'}</WidgetNotice>;
  }
  return (
    <div className="flex flex-1 flex-col justify-center gap-2 py-1">
      {stages.map((stage, i) => (
        <div key={stage.label}>
          <div className="flex items-baseline justify-between gap-2 text-xs">
            <span className="truncate text-muted-foreground">{stage.label}</span>
            <span className="shrink-0 font-medium tabular-nums">
              {stage.value === null ? '—' : Math.round(stage.value).toLocaleString('en-US')}
            </span>
          </div>
          <div className="mt-0.5 h-2 w-full rounded-sm bg-muted">
            <div
              className="h-2 rounded-sm bg-[hsl(var(--chart-1))]"
              style={{ width: `${Math.max(ratios[i]!.ratio * 100, stage.value === null ? 0 : 1)}%` }}
            />
          </div>
          {ratios[i]!.ofPrevious !== null && (
            <div className="mt-0.5 text-[0.65rem] text-muted-foreground">
              {ratios[i]!.ofPrevious!.toFixed(1)}% of {stages[i - 1]!.label}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * One percentage on a 0–100 dial.
 *
 * A hand-drawn SVG arc rather than recharts' RadialBarChart: a semicircle with
 * one value needs no chart machinery, and an SVG with a viewBox scales to
 * whatever the panel was dragged to for free — no ResponsiveContainer, no
 * remount nonce.
 */
function GaugeWidget({
  widget,
  source,
}: {
  widget: ReportingWidget;
  source: WidgetDataSource;
}): React.JSX.Element {
  const raw = source.percent(widget.metricKey);
  const pct = clampPercent(raw);
  const label = source.metrics.find((m) => m.key === widget.metricKey)?.label ?? widget.metricKey;

  // Semicircle from 180° to 0°. r=42 in a 100×56 viewBox leaves room for the cap.
  const R = 42;
  const CIRC = Math.PI * R;
  return (
    <div className="flex flex-1 flex-col items-center justify-center">
      <svg viewBox="0 0 100 58" className="h-full max-h-40 w-full" role="img" aria-label={label}>
        <path
          d={`M 8 50 A ${R} ${R} 0 0 1 92 50`}
          fill="none"
          stroke="hsl(var(--muted))"
          strokeWidth={9}
          strokeLinecap="round"
        />
        {/* ⚠️ No arc at all when unmeasured — a needle resting at zero would say
            "you won none of it", which is a different claim from "we could not
            measure it". */}
        {pct !== null && (
          <path
            d={`M 8 50 A ${R} ${R} 0 0 1 92 50`}
            fill="none"
            stroke="hsl(var(--chart-1))"
            strokeWidth={9}
            strokeLinecap="round"
            strokeDasharray={`${(pct / 100) * CIRC} ${CIRC}`}
          />
        )}
        <text
          x="50"
          y="46"
          textAnchor="middle"
          className="fill-foreground"
          style={{ fontSize: '16px', fontWeight: 600 }}
        >
          {pct === null ? '—' : `${Math.round(pct * 10) / 10}%`}
        </text>
      </svg>
      {pct === null && !source.loading && (
        <span className="text-xs text-muted-foreground">Not reported for this scope</span>
      )}
    </div>
  );
}

const SLICE_COLOURS = [
  'hsl(var(--chart-1))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
];

/** The dimension a dimensional widget is actually plotting. */
function resolveDimension(widget: ReportingWidget, source: WidgetDataSource): string | null {
  if (widget.dimension && source.dimensions.some((d) => d.key === widget.dimension)) {
    return widget.dimension;
  }
  return source.dimensions[0]?.key ?? null;
}

/**
 * Shared setup for the pie and the ranked bar: resolve the dimension, ask the
 * dashboard for the split, and run BOTH gates — the mixed-currency refusal and
 * the top-N plan. Returning one shape keeps the two renderers from drifting
 * apart about what they may draw.
 */
function useDimensionalPlan(widget: ReportingWidget, source: WidgetDataSource) {
  const dimension = resolveDimension(widget, source);
  const data = dimension
    ? source.dimensional(widget.metricKey, dimension)
    : null;
  if (!data) {
    return { verdict: { ok: false as const, reason: 'This dashboard has no dimensions to split by.' } };
  }
  if (data.loading && data.slices.length === 0) {
    return { verdict: { ok: false as const, reason: 'loading…' } };
  }
  const verdict = canPlotDimensional({
    format: data.format,
    mixedCurrency: data.mixedCurrency,
    currencies: data.currencies,
    sliceCount: data.slices.filter((s) => s.value !== null).length,
    unavailable: data.unavailable,
  });
  if (!verdict.ok) return { verdict };
  const plan = planSlices(data.slices, clampTopN(widget.topN));
  return {
    verdict,
    plan,
    data,
    dropped: unmeasuredCount(data.slices),
    format: (value: number) => formatMetricValue(data.format, value, data.currency),
  };
}

function DimensionalFooter({
  dropped,
  label,
}: {
  dropped: number;
  label: string;
}): React.JSX.Element | null {
  if (dropped === 0) return null;
  // Saying how many values were left out beats silently showing a shorter list —
  // an unmeasured value is not a zero, and the reader should know it existed.
  return (
    <p className="shrink-0 pt-1 text-[0.65rem] text-muted-foreground">
      {dropped} {label}
      {dropped === 1 ? '' : 's'} not reported and left out
    </p>
  );
}

/** Top-N dimension values by a metric, as horizontal bars. */
function RankedBarWidget({
  widget,
  source,
}: {
  widget: ReportingWidget;
  source: WidgetDataSource;
}): React.JSX.Element {
  const resolved = useDimensionalPlan(widget, source);
  if (!resolved.verdict.ok) return <WidgetNotice>{resolved.verdict.reason}</WidgetNotice>;
  const { plan, format } = resolved;
  const rows = plan!.shown;
  const max = rows[0]?.value ?? 0;
  const dimensionLabel =
    source.dimensions.find((d) => d.key === resolveDimension(widget, source))?.label ?? 'value';

  return (
    // ⚠️ `min-h-0` IS LOAD-BEARING, not tidying. A flex child defaults to
    // `min-height: auto`, which means its CONTENT sets its floor — so with the
    // top-N ceiling at 300 the list would push this box past the panel and the
    // whole canvas would grow around it, exactly the failure the table panels'
    // fill CSS exists to prevent. With it, the box is bounded by the panel and
    // the scroll region below actually scrolls.
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Hand-drawn bars rather than a recharts BarChart: a ranked list is a
          list, and this keeps every label readable and left-aligned at any panel
          width instead of a Y-axis truncating them. It scrolls INTERNALLY —
          a 300-row ranked bar is taller than any panel, and the alternative
          (clipping) would present a truncated list as if it were complete. */}
      <div className="scroll-subtle min-h-0 flex-1 space-y-1 overflow-y-auto pr-1">
        {rows.map((row, i) => (
          <div key={row.key} className="text-xs">
            <div className="flex items-baseline justify-between gap-2">
              <span className="truncate text-muted-foreground" title={row.label}>
                {i + 1}. {row.label}
              </span>
              <span className="shrink-0 font-medium tabular-nums">{format!(row.value)}</span>
            </div>
            <div className="mt-0.5 h-1.5 w-full rounded-sm bg-muted">
              <div
                className="h-1.5 rounded-sm bg-[hsl(var(--chart-1))]"
                style={{ width: `${max > 0 ? Math.max((row.value / max) * 100, 1) : 0}%` }}
              />
            </div>
          </div>
        ))}
      </div>
      <DimensionalFooter dropped={resolved.dropped!} label={dimensionLabel.toLowerCase()} />
    </div>
  );
}

/** One metric split by a dimension — a donut with a top-N + "Other" legend. */
function PieWidget({
  widget,
  source,
}: {
  widget: ReportingWidget;
  source: WidgetDataSource;
}): React.JSX.Element {
  const resolved = useDimensionalPlan(widget, source);
  if (!resolved.verdict.ok) return <WidgetNotice>{resolved.verdict.reason}</WidgetNotice>;
  const { plan, format } = resolved;
  const slices = [...plan!.shown, ...(plan!.other ? [plan!.other] : [])];
  const total = slices.reduce((n, s) => n + s.value, 0);
  const dimensionLabel =
    source.dimensions.find((d) => d.key === resolveDimension(widget, source))?.label ?? 'value';

  const config: ChartConfig = Object.fromEntries(
    slices.map((s, i) => [s.key, { label: s.label, color: SLICE_COLOURS[i % SLICE_COLOURS.length] }]),
  );

  return (
    <div className="flex flex-1 flex-col">
      <div className="reporting-chart-panel-grow min-h-0">
        <ChartContainer config={config} className="aspect-auto h-full w-full">
          <PieChart>
            <ChartTooltip
              content={
                <ChartTooltipContent
                  formatter={(value, name) => (
                    <span className="font-mono font-medium tabular-nums text-foreground">
                      {name}: {format!(Number(value))}
                      {total > 0 && ` (${((Number(value) / total) * 100).toFixed(1)}%)`}
                    </span>
                  )}
                />
              }
            />
            <Pie
              data={slices}
              dataKey="value"
              nameKey="label"
              innerRadius="45%"
              outerRadius="80%"
              isAnimationActive={false}
              stroke="hsl(var(--background))"
              strokeWidth={2}
            >
              {slices.map((s, i) => (
                <Cell key={s.key} fill={SLICE_COLOURS[i % SLICE_COLOURS.length]} />
              ))}
            </Pie>
          </PieChart>
        </ChartContainer>
      </div>
      <DimensionalFooter dropped={resolved.dropped!} label={dimensionLabel.toLowerCase()} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// The registry
// ---------------------------------------------------------------------------

export type WidgetRenderer = (props: {
  widget: ReportingWidget;
  source: WidgetDataSource;
}) => React.JSX.Element;

/**
 * viz type → renderer. Adding a Phase-2 representation is one entry here plus
 * one in the leaf's catalogue; nothing else in the framework changes.
 */
const WIDGET_RENDERERS: Record<WidgetVizType, WidgetRenderer> = {
  singleNumber: SingleNumberWidget,
  trend: TrendWidget,
  funnel: FunnelWidget,
  gauge: GaugeWidget,
  rankedBar: RankedBarWidget,
  pie: PieWidget,
};

/**
 * Resolve a renderer, or null for a type this build does not register.
 *
 * The decoder already drops unresolvable widgets, so this is belt and braces —
 * but a registry that throws on an unknown key would turn a stale preference
 * into a white screen, which is exactly the failure mode this codebase keeps
 * designing against.
 */
export function widgetRenderer(vizType: string): WidgetRenderer | null {
  return isWidgetVizType(vizType) ? WIDGET_RENDERERS[vizType] : null;
}

// ---------------------------------------------------------------------------
// Per-widget configuration
// ---------------------------------------------------------------------------

function ConfigRow({
  label,
  hint,
  checked,
  disabled,
  title,
  onClick,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  disabled?: boolean;
  title?: string;
  onClick: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={checked}
      disabled={disabled}
      onClick={onClick}
      title={title}
      className={cn(
        'flex w-full items-start gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-accent',
        disabled && 'cursor-not-allowed opacity-40',
      )}
    >
      <span
        className={cn(
          'mt-1 h-2 w-2 shrink-0 rounded-full border',
          checked ? 'border-kyck-600 bg-kyck-600' : 'border-muted-foreground/50',
        )}
        aria-hidden
      />
      <span className="min-w-0">
        <span className="block">{label}</span>
        {hint && <span className="block text-xs text-muted-foreground">{hint}</span>}
      </span>
    </button>
  );
}

/**
 * The gear popover: what this widget shows and how. Changing anything
 * re-renders that widget in place — its position and size are untouched, because
 * the layout is keyed on the widget's id, not on what it happens to display.
 */
export function WidgetConfigMenu({
  widget,
  source,
  onChange,
  onRemove,
}: {
  widget: ReportingWidget;
  source: WidgetDataSource;
  onChange: (patch: Partial<Omit<ReportingWidget, 'id'>>) => void;
  onRemove: () => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside([ref], () => setOpen(false), open);

  const vizDef = widgetVizTypeDef(widget.vizType);
  const comparisonApplies = vizDef?.supportsComparison ?? false;
  // ⚠️ A GAUGE offers only the PERCENTAGE metrics — a dial runs 0–100, so a cost
  // or a click count on one would be meaningless. The FUNNEL offers none at all:
  // its three stages ARE the funnel.
  const metricOptions = vizDef?.percentOnly ? source.percentMetrics : source.metrics;
  const showsDimension = (vizDef?.needsDimension ?? false) && source.dimensions.length > 0;
  const activeDimension = resolveDimension(widget, source);

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label="Configure widget"
        title="Choose the metric and how it is shown"
        className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground/60 hover:bg-accent hover:text-foreground"
      >
        <Settings2 className="h-3.5 w-3.5" aria-hidden />
      </button>
      {open && (
        <div
          role="menu"
          className="scroll-subtle absolute right-0 z-30 mt-1 max-h-96 w-64 overflow-y-auto rounded-md border border-border bg-popover p-1 text-sm shadow-lg"
        >
          <div className="px-2 pb-1 pt-1.5 text-xs font-medium text-muted-foreground">Show as</div>
          {WIDGET_VIZ_TYPES.map((v) => (
            <ConfigRow
              key={v.key}
              label={v.label}
              hint={v.hint}
              checked={v.key === widget.vizType}
              onClick={() => onChange({ vizType: v.key })}
            />
          ))}

          {vizDef?.needsMetric && (
            <>
              <div className="mt-1 border-t border-border px-2 pb-1 pt-2 text-xs font-medium text-muted-foreground">
                Metric
              </div>
              {metricOptions.map((m) => (
                <ConfigRow
                  key={m.key}
                  label={m.label}
                  hint={m.hint}
                  checked={m.key === widget.metricKey}
                  onClick={() => onChange({ metricKey: m.key })}
                />
              ))}
              {metricOptions.length === 0 && (
                <p className="px-2 pb-1 text-xs text-muted-foreground">
                  This dashboard has no percentage metrics to show on a dial.
                </p>
              )}
            </>
          )}

          {showsDimension && (
            <>
              <div className="mt-1 border-t border-border px-2 pb-1 pt-2 text-xs font-medium text-muted-foreground">
                Split by
              </div>
              {source.dimensions.map((d) => (
                <ConfigRow
                  key={d.key}
                  label={d.label}
                  checked={d.key === activeDimension}
                  onClick={() => onChange({ dimension: d.key })}
                />
              ))}
              {/* A free number up to the ceiling rather than a fixed set of
                  choices: "top 10" and "every one of the 187 locations" are both
                  legitimate questions, and a ranked bar can answer the second
                  because it scrolls inside its panel. The bounds come from the
                  leaf's constants, so the control and the stored value can't
                  disagree about what is allowed. */}
              <label className="mt-1 flex items-center justify-between gap-2 px-2 py-1.5 text-sm">
                <span>
                  Show top
                  <span className="ml-1 text-xs text-muted-foreground">
                    ({MIN_WIDGET_TOP_N}–{MAX_WIDGET_TOP_N})
                  </span>
                </span>
                <input
                  type="number"
                  min={MIN_WIDGET_TOP_N}
                  max={MAX_WIDGET_TOP_N}
                  value={clampTopN(widget.topN)}
                  onChange={(e) => onChange({ topN: clampTopN(Number(e.target.value)) })}
                  className="h-7 w-20 rounded border border-input bg-background px-1.5 text-right text-sm"
                />
              </label>
            </>
          )}

          {comparisonApplies && (
            <>
              <div className="mt-1 border-t border-border pt-2" />
              <label
                className={cn(
                  'flex cursor-pointer items-start gap-2 rounded px-2 py-1.5 hover:bg-accent',
                  !source.comparisonAvailable && 'cursor-not-allowed opacity-50',
                )}
                title={
                  source.comparisonAvailable
                    ? 'Show the change against the previous equivalent period'
                    : source.comparisonUnavailableReason
                }
              >
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={widget.showComparison}
                  disabled={!source.comparisonAvailable}
                  onChange={(e) => onChange({ showComparison: e.target.checked })}
                />
                <span>
                  <span className="block">Compare to previous period</span>
                  {!source.comparisonAvailable && source.comparisonUnavailableReason && (
                    <span className="block text-xs text-muted-foreground">
                      {source.comparisonUnavailableReason}
                    </span>
                  )}
                </span>
              </label>
            </>
          )}

          <div className="mt-1 border-t border-border pt-1">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onRemove();
              }}
              className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden />
              Remove widget
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The card + the region
// ---------------------------------------------------------------------------

/**
 * The widget's own card. Like the KPI tiles it hosts the grid's drag GRIP in its
 * own header rather than wearing the shell's strip — a widget can be small, and
 * a strip floating above a compact scorecard reads as noise. The grip stops
 * click but never mousedown; see ChartGripHandle.
 */
function WidgetCard({
  widget,
  source,
  onChange,
  onRemove,
}: {
  widget: ReportingWidget;
  source: WidgetDataSource;
  onChange: (patch: Partial<Omit<ReportingWidget, 'id'>>) => void;
  onRemove: () => void;
}): React.JSX.Element {
  const Renderer = widgetRenderer(widget.vizType);
  const label =
    source.metrics.find((m) => m.key === widget.metricKey)?.label ?? widget.metricKey;

  return (
    <div className="flex flex-col rounded-lg border border-border bg-card p-4">
      <div className="flex items-start justify-between gap-2">
        {/* The shared panel-title treatment — a widget's metric label is the
            only thing saying what the figure below it IS, so it reads at the
            same weight/contrast as every other panel heading. See PanelTitle. */}
        <PanelTitle title={label}>{label}</PanelTitle>
        <div className="flex shrink-0 items-center gap-0.5">
          <ChartGripHandle
            title={`Drag to move ${label} · resize from its bottom-right corner`}
          />
          {widget.vizType === 'trend' && (
            <ChartStyleToggle
              value={source.style(widget.metricKey)}
              onChange={(s) => source.onStyleChange(widget.metricKey, s)}
            />
          )}
          <WidgetConfigMenu
            widget={widget}
            source={source}
            onChange={onChange}
            onRemove={onRemove}
          />
        </div>
      </div>
      <div className="reporting-chart-panel-grow mt-2">
        {Renderer ? (
          <Renderer widget={widget} source={source} />
        ) : (
          // Unreachable while decoding drops unknown types, but a registry miss
          // must degrade to a message rather than to a crashed page.
          <p className="text-sm text-muted-foreground">
            This widget type isn’t available in this version.
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Map the widget instances onto canvas panels — folded by each dashboard into
 * the ONE grid alongside its tiles, charts and tables.
 */
export function buildWidgetPanels({
  widgets,
  source,
  onWidgetsChange,
}: {
  widgets: readonly ReportingWidget[];
  source: WidgetDataSource;
  onWidgetsChange: (next: ReportingWidget[]) => void;
}): ChartPanel[] {
  return widgets.map((widget) => ({
    // ⚠️ The panel key is the widget's ID, never its metric — so reconfiguring a
    // widget keeps the box exactly where the user put it, and two widgets on the
    // same metric are two independent panels. Kind-qualified because the canvas
    // holds four populations of panel in one coordinate space.
    key: canvasPanelKey('widget', widget.id),
    title: source.metrics.find((m) => m.key === widget.metricKey)?.label ?? widget.metricKey,
    defaultW: 3,
    defaultH: 4,
    minW: 2,
    minH: 3,
    providesOwnGrip: true,
    node: (
      <WidgetCard
        widget={widget}
        source={source}
        onChange={(patch) => onWidgetsChange(updateWidget(widgets, widget.id, patch))}
        onRemove={() => onWidgetsChange(removeWidget(widgets, widget.id))}
      />
    ),
  }));
}

/**
 * "Add widget" — the affordance that puts a new panel on the canvas.
 *
 * It lives in the page's canvas TOOLBAR rather than owning a region of its own,
 * because there are no regions any more: the widgets share one grid with the
 * tiles, charts and tables, so there is no empty area to host a prompt and the
 * button is the whole affordance. That is also why it is deliberately labelled
 * rather than an icon — it is the only entry point to the feature.
 */
export function AddWidgetButton({
  widgets,
  onWidgetsChange,
  defaultMetricKey,
  className,
}: {
  widgets: readonly ReportingWidget[];
  onWidgetsChange: (next: ReportingWidget[]) => void;
  /** What a new widget starts from — a sensible metric for this dashboard. */
  defaultMetricKey: string;
  className?: string;
}): React.JSX.Element {
  return (
    <Button
      variant="outline"
      size="sm"
      className={className}
      title="Add a single number or a trend for any metric, then drag it anywhere on the canvas"
      onClick={() => onWidgetsChange([...widgets, createWidget(widgets, defaultMetricKey)])}
    >
      <Plus className="mr-1 h-3.5 w-3.5" aria-hidden />
      Add widget
    </Button>
  );
}
